import { Embedding } from './embedding'
import type { EmbeddingOptions, OnReadyCallback } from './types'

/**
 * Root SDK entry. Exposes `embed(journeyId)` to build an `Embedding`
 * and `onReady(cb)` for the async CDN install pattern.
 */
export class Epilot {
  private $options: EmbeddingOptions

  constructor(options: EmbeddingOptions = {}) {
    this.$options = options
  }

  /**
   * Returns a fresh `Epilot` instance. Kept for API continuity with the
   * SDK's original shape — `new Epilot()` is the idiomatic path; use
   * `init()` when you want a readable signal of "start here".
   */
  init(): Epilot {
    return new Epilot()
  }

  /**
   * Returns an `Embedding` builder for the given Journey id. Every call
   * returns a new instance — multiple Journeys can be embedded on the
   * same page (iframe backend only; the web component is restricted to
   * one registration per page).
   */
  embed(journeyId: string): Embedding {
    return new Embedding(journeyId, this.$options)
  }

  /**
   * Invokes `cb` with the SDK instance. When the SDK is loaded synchronously
   * the callback runs immediately; when loaded via the async CDN stub, the
   * stub queues callbacks and this method drains them on init (see
   * `sdk-entry.ts` in the journey-embed repo).
   *
   * Errors thrown by `cb` are caught and logged so one bad callback cannot
   * break the rest of the queue.
   */
  onReady(cb: OnReadyCallback): this {
    try {
      cb(this)
    } catch (err) {
      console.error('[epilot] onReady callback threw', err)
    }
    return this
  }
}

export default Epilot
